import React, { useEffect, useState } from 'react';
import { audioEngine } from '../services/audioEngine';
import { poolStats, poolFree, soundingStats, aheadStats } from '../services/audioPool';
import { stageAudio, cityAudio } from '../city/stageBridge';
import { useCityStore } from '../city/cityStore';
import { volumeWorks, musicHushed, voiceHolders } from '../city/hearing';
import { atmosDebug } from '../services/atmosphere';

// AudioDebug — `?audiodebug=1`: what every player is doing, on the phone itself
// (2026-10-29: on an iPhone the radio did not come back after a street source,
// and the plaza's concert never took over; nobody can open a console there).
//
//   station  layer / paused / ready / who holds it / play() refusals / pulse
//   pool     blessed players idle · lent · handed out UNBLESSED (silent on iOS)
//   stage    the song playing now (its element sounding or not), presence
//   street   the street music heard here
//   atmos    the city's music heard through its places (services/atmosphere):
//            on or off here (off on iOS), the context, players routed / lent /
//            left plain, any bucket that would not let us read it
//   voices   volume works here or not (iOS: no), and whether the music is
//            muted for someone speaking right now, and for whom (city/hearing)
//   sounding  each kind of player that should be heard right now (talk = the street's
//            conversations, story, score, stage, street, heli): how many, how many
//            really playing, paused, still loading, unblessed, retried, and why refused
const ON = typeof window !== 'undefined' && new URLSearchParams(window.location.search).get('audiodebug') === '1';

// Out of the way (2026-10-30: over the top-left HUD it hid what Khari was
// pressing): mid-left, small, and collapsible to a pill. Only the pill takes
// taps; the readout itself lets every touch through.
const wrap = {
  position: 'fixed', left: 6, top: '42%', zIndex: 9999, display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 4,
  pointerEvents: 'none',
};
const pill = {
  pointerEvents: 'auto', cursor: 'pointer', border: '1px solid rgba(157,255,125,0.5)', borderRadius: 999,
  background: 'rgba(0,0,0,0.7)', color: '#9dff7d', font: '600 10px ui-monospace, Menlo, monospace', padding: '3px 8px',
};
const box = {
  pointerEvents: 'none', background: 'rgba(0,0,0,0.62)', color: '#9dff7d', font: '9.5px ui-monospace, Menlo, monospace', lineHeight: 1.35,
  padding: '4px 6px', borderRadius: 6, maxWidth: 'min(240px, 62vw)', whiteSpace: 'pre-wrap',
};
const remembered = () => { try { return sessionStorage.getItem('audiodebug:open') !== '0'; } catch { return true; } };

export default function AudioDebug() {
  const [text, setText] = useState('');
  const [open, setOpen] = useState(remembered);
  const toggle = () => setOpen((o) => { try { sessionStorage.setItem('audiodebug:open', o ? '0' : '1'); } catch { /* private mode */ } return !o; });
  useEffect(() => {
    if (!ON) return undefined;
    const tick = () => {
      const s = audioEngine.debugState();
      const cs = useCityStore.getState();
      setText([
        `station ${s.layer} · ${s.paused ? 'PAUSED' : 'playing'} · ready ${s.ready}`,
        `  held ${s.held ? 'yes' : 'no'} [${s.holders.join(', ') || '-'}] · refusals ${s.failures} · pulse ${s.pulse ? 'ON' : 'off'}`,
        `pool free ${poolFree()} · lent ${poolStats.lent} · unblessed ${poolStats.plain} · clips fetched ahead ${aheadStats.fetched} (failed ${aheadStats.failed})`,
        `stage ${stageAudio.key || '-'} · ${stageAudio.playing ? 'SOUNDING' : 'silent'} · presence ${(cityAudio.stagePresence || 0).toFixed(2)}`,
        `  now ${cs.stageNow ? `${cs.stageNow.name}: ${cs.stageNow.title}` : '-'}`,
        ...(stageAudio.dbg ? [`  el ${stageAudio.dbg.paused ? 'PAUSED' : 'running'} · ready ${stageAudio.dbg.ready} · net ${stageAudio.dbg.net} · err ${stageAudio.dbg.err ?? '-'} · muted ${stageAudio.dbg.muted ? 'y' : 'n'}`,
          `  play ${stageAudio.dbg.playErr || 'ok'} · from ${stageAudio.dbg.host}${stageAudio.dbg.rel ? ' (RELATIVE url)' : ''}`] : []),
        `street ${cs.soundNow ? `${cs.soundNow.title || '?'} (${cs.soundNow.where || ''})` : '-'}`,
        `voices first · volume ${volumeWorks ? 'works' : 'IGNORED (iOS)'} · music ${musicHushed() ? 'MUTED' : 'on'} [${voiceHolders().join(', ') || '-'}]`,
        (() => { const a = atmosDebug(); return `atmos ${a.on ? a.state : 'off here'} · routed ${a.routed} · lent ${a.lent} · plain ${a.plain}${a.plainBuckets.length ? ` · unreadable ${a.plainBuckets.join(',')}` : ''}`; })(),
        ...soundingStats().map((x) => `${x.label} ${x.n} · playing ${x.playing} · paused ${x.paused} · loading ${x.loading} · unblessed ${x.unblessed} · retries ${x.retries}${x.errs ? ` · ${x.errs}` : ''}`),
      ].join('\n'));
    };
    const id = setInterval(tick, 500);
    tick();
    return () => clearInterval(id);
  }, []);
  if (!ON) return null;
  return (
    <div style={wrap}>
      <button type="button" style={pill} onClick={toggle} aria-expanded={open}>{open ? 'audio ▾' : 'audio ▸'}</button>
      {open && <div style={box}>{text}</div>}
    </div>
  );
}
